import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    Badge,
    Box,
    Button,
    Container,
    Flex,
    Grid,
    Group,
    MultiSelect,
    Portal,
    Select,
    Tabs,
    Text,
    TextInput,
    Textarea,
    Tooltip,
} from '@mantine/core';
import {
    PhoneNumberInput,
    phoneValidator,
} from '../../../common/phoneNumberInput/PhoneNumberInput';
import {
    faChartMixed,
    faList,
    faSpinnerThird,
} from '@fortawesome/pro-solid-svg-icons';
import {
    getAvailableAgents,
    getCampaigns,
    getLinkGroups,
    getMessageGroups,
    getShortlinks,
} from '../../../../http/apiClient';
import { useDebouncedCallback, useLocalStorage } from '@mantine/hooks';
import { useMemo, useState } from 'react';

import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MantineReactTable } from 'mantine-react-table';
import { SearchableSelect } from '../../../common';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';
import useSendTestMessage from '../../../../http/mutations/tools/useSendTestMessage';

const DestinationNumber = ({ form }) => {
    return (
        <PhoneNumberInput
            form={form}
            label={'Destination number'}
            withAsterisk
        />
    );
};

const OriginatingNumber = ({ form }) => {
    return (
        <SearchableSelect
            label={'Originating number'}
            placeholder='Select originating number'
            filters={['show_number']}
            queryHttpFn={(queryParams) =>
                getAvailableAgents({
                    routeId: null,
                    pagination: queryParams,
                })
            }
            queryKey={'campaign_agents'}
            optionsMapFn={(x) => ({
                value: x.id.toString(),
                label: x.subName
                    ? `${x.name}-${x.subName.replace('usb-', '')}`
                    : x.name,
                //disabled: !x.available,
            })}
            withAsterisk
            maxValues={1}
            renderOption={({ option, queryData }) => {
                const dto = queryData;
                const agentName = dto?.subName
                    ? `${dto.name}-${dto.subName.replace('usb-', '')}`
                    : dto?.name;
                return (
                    <Group gap='sm'>
                        <div>
                            <Text size='sm'>
                                {agentName}{' '}
                                {dto.available ? (
                                    <Badge
                                        size={'xs'}
                                        color={'teal.6'}>
                                        Available
                                    </Badge>
                                ) : (
                                    <Badge
                                        size={'xs'}
                                        color={'red.6'}>
                                        {dto.assignment?.campaignName}
                                    </Badge>
                                )}
                            </Text>
                            {dto.assignment ? (
                                <Text
                                    size='xs'
                                    opacity={0.7}>
                                    Campaign: {dto.assignment?.campaignName}
                                </Text>
                            ) : null}
                        </div>
                    </Group>
                );
            }}
            key={form.key('agentId')}
            {...form.getInputProps('agentId')}
        />
    );
};

const ShortLink = ({ form, ...props }) => {
    return (
        <SearchableSelect
            label={'Short link'}
            placeholder='Select short link'
            queryHttpFn={(queryParams) => getShortlinks(queryParams)}
            queryKey={'short_links'}
            optionsMapFn={(x) => ({
                label: x.domain,
                value: x.id.toString(),
            })}
            key={form.key('shortLinkId')}
            {...form.getInputProps('shortLinkId')}
            {...props}
        />
    );
};

const SmsTestingView = ({ ...props }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    //console.log(userProfile);
    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            type: 'campaign',
            countryCode: '+1',
            phoneNumber: null,
            agentId: null,
            campaignId: null,
            shortLinkId: null,
            messageGroupId: null,
            linkGroupId: null,
            messageContent: null,
        },

        transformValues: (values) => {
            const baseValues = {
                type: values.type,
                destinationNumber: `${values.countryCode}${values.phoneNumber}`,
                agentId: values.agentId,
            };

            switch (values.type) {
                case 'campaign':
                    return {
                        ...baseValues,
                        campaignId: values.campaignId,
                        shortLinkId: values.shortLinkId,
                    };
                case 'group':
                    return {
                        ...baseValues,
                        messageGroupId: values.messageGroupId,
                        linkGroupId: values.linkGroupId,
                        shortLinkId: values.shortLinkId,
                    };
                case 'static':
                    return {
                        ...baseValues,
                        messageContent: values.messageContent,
                    };
            }
        },

        validate: {
            phoneNumber: phoneValidator,
            agentId: (value) => {
                const { message } = genericValidator(
                    'Originating number',
                    value,
                    {
                        required: true,
                    }
                );
                return message;
            },
            messageContent: (value, values) => {
                const { message } = genericValidator('Message content', value, {
                    required: values.type === 'static',
                });
                return message;
            },
            messageGroupId: (value, values) => {
                const { message } = genericValidator('Message group', value, {
                    required: values.type === 'group',
                });
                return message;
            },
            linkGroupId: (value, values) => {
                const { message } = genericValidator('Link group', value, {
                    required: values.type === 'group',
                });
                return message;
            },
            shortLinkId: (value, values) => {
                const { message } = genericValidator('Shortlink', value, {
                    required:
                        values.type === 'group' &&
                        hasRole(userProfile, 'SHORT_LINKS'),
                });
                return message;
            },
            /*emailOrUsername: isNotEmpty('Username or email is required'),
                    password: (val) =>
                        val.length <= 6
                            ? 'Password should include at least 6 characters'
                            : null,*/
        },
    });

    const { isPending, mutate: sendTestMessage } = useSendTestMessage();

    const onSubmit = (values) => {
        console.log(values);
        sendTestMessage(values);
    };

    return (
        <Container fluid>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Tabs
                    value={form.values.type}
                    onChange={(value) => form.setFieldValue('type', value)}>
                    <Tabs.List>
                        <Tabs.Tab
                            value='campaign'
                            leftSection={<FontAwesomeIcon icon={faList} />}>
                            Campaign
                        </Tabs.Tab>
                        <Tabs.Tab
                            value='group'
                            leftSection={
                                <FontAwesomeIcon icon={faChartMixed} />
                            }>
                            Message Group
                        </Tabs.Tab>
                        <Tabs.Tab
                            value='static'
                            leftSection={
                                <FontAwesomeIcon icon={faChartMixed} />
                            }>
                            Static Message
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel
                        value='group'
                        py={'sm'}>
                        <Grid>
                            <Grid.Col span={6}>
                                <DestinationNumber form={form} />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <OriginatingNumber form={form} />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <SearchableSelect
                                    label={'Message Group'}
                                    placeholder='Select message group'
                                    queryHttpFn={(queryParams) =>
                                        getMessageGroups({
                                            type: 'sms',
                                            pagination: queryParams,
                                        })
                                    }
                                    queryKey={'message_groups'}
                                    optionsMapFn={(x) => ({
                                        label: x.name || 'test',
                                        value: x.id.toString(),
                                    })}
                                    withAsterisk
                                    key={form.key('messageGroupId')}
                                    {...form.getInputProps('messageGroupId')}
                                />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <SearchableSelect
                                    label={'Link Group'}
                                    placeholder='Select link group'
                                    queryHttpFn={(queryParams) =>
                                        getLinkGroups({
                                            pagination: queryParams,
                                        })
                                    }
                                    queryKey={'link_groups'}
                                    optionsMapFn={(x) => ({
                                        label: x.name || 'test',
                                        value: x.id.toString(),
                                    })}
                                    withAsterisk
                                    key={form.key('linkGroupId')}
                                    {...form.getInputProps('linkGroupId')}
                                />
                            </Grid.Col>
                            {hasRole(userProfile, 'SHORT_LINKS') ? (
                                <Grid.Col span={6}>
                                    <ShortLink
                                        form={form}
                                        withAsterisk
                                    />
                                </Grid.Col>
                            ) : null}
                        </Grid>
                    </Tabs.Panel>

                    <Tabs.Panel
                        value='campaign'
                        py={'sm'}>
                        <Grid>
                            <Grid.Col span={6}>
                                <DestinationNumber form={form} />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <OriginatingNumber form={form} />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <SearchableSelect
                                    label={'Campaign'}
                                    placeholder='Select campaign'
                                    queryHttpFn={(queryParams) =>
                                        getCampaigns({
                                            types: ['Sms', 'Rvm'],
                                            pagination: queryParams,
                                        })
                                    }
                                    queryKey={'campaigns'}
                                    optionsMapFn={(x) => ({
                                        label: x.name,
                                        value: x.id.toString(),
                                    })}
                                    withAsterisk
                                    key={form.key('campaignId')}
                                    {...form.getInputProps('campaignId')}
                                />
                            </Grid.Col>
                            {hasRole(userProfile, 'SHORT_LINKS') ? (
                                <Grid.Col span={6}>
                                    <ShortLink form={form} />
                                </Grid.Col>
                            ) : null}
                        </Grid>
                    </Tabs.Panel>

                    <Tabs.Panel
                        value='static'
                        py={'sm'}>
                        <Grid>
                            <Grid.Col span={6}>
                                <DestinationNumber form={form} />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <OriginatingNumber form={form} />
                            </Grid.Col>

                            <Grid.Col span={12}>
                                <Textarea
                                    autosize
                                    minRows={10}
                                    maxRows={10}
                                    placeholder='Message content'
                                    key={form.key('messageContent')}
                                    {...form.getInputProps('messageContent')}
                                />
                            </Grid.Col>
                        </Grid>
                    </Tabs.Panel>
                </Tabs>
                <Group
                    justify='flex-end'
                    align='flex-end'>
                    <Button
                        size={'xs'}
                        type={'submit'}
                        disabled={isPending || !form.isValid()}
                        rightSection={
                            isPending ? (
                                <FontAwesomeIcon
                                    icon={faSpinnerThird}
                                    spin
                                />
                            ) : null
                        }>
                        Send message
                    </Button>
                </Group>
            </form>
        </Container>
    );
};
export default SmsTestingView;
